import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { usePendingCount } from '../../hooks/useOfflineSync';
import { detect } from '../../services/installApp';
import { useInstallFlow } from '../../hooks/useInstallFlow';
import { InstallInstructions } from '../InstallInstructions/InstallInstructions';
import './InstallPrompt.css';

const DISMISSED_KEY = 'pwa_install_dismissed_until';
// Si el usuario elige "Ahora no", el banner vuelve a aparecer pasadas 5 HORAS,
// sin importar en qué pantalla esté ni esperar a una recarga.
const SNOOZE_MS = 5 * 60 * 60 * 1000;
// Delay mínimo desde la carga para que la app pinte antes y el banner no
// parpadee sobre un fondo a medio renderizar.
const INITIAL_DELAY_MS = 300;

// Banner de instalación (bottom-sheet). Solo aparece DENTRO de la app (tras
// login) y si no está instalada. El botón "Instalar app" usa el MISMO flujo
// unificado que el login y Ajustes: instalación nativa donde el navegador lo
// permite (Chrome/Edge/Samsung), o el modal de instrucciones en el resto
// (iOS Safari, Firefox…). En el login no se muestra: allí hay un botón fijo.
export function InstallPrompt() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const install = useInstallFlow();
  const [show, setShow] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // En iOS, "Añadir a pantalla de inicio" crea un contenedor de storage separado
  // del Safari de la pestaña → la cola en IndexedDB quedaría huérfana. Si hay
  // pendientes, bloqueamos la instalación con un aviso para esperar a la sync.
  const pendingCount = usePendingCount();
  const isIos = detect().platform === 'ios';
  const iosBlocked = isIos && pendingCount > 0;

  const scheduleShow = (delayMs: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (!install.hidden) setShow(true);
    }, Math.max(0, delayMs));
  };

  useEffect(() => {
    // Solo tras login y si no está instalada.
    if (!isAuthenticated || install.hidden) {
      setShow(false);
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }
    // Respetamos el snooze: si recargamos en mitad de uno activo, esperamos el
    // tiempo restante; si ya expiró, mostramos tras el delay inicial corto.
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    const snoozeExpiresAt = dismissed ? Number(dismissed) : 0;
    const remainingSnooze = Math.max(0, snoozeExpiresAt - Date.now());
    scheduleShow(remainingSnooze > 0 ? remainingSnooze : INITIAL_DELAY_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, install.hidden]);

  const handleInstall = async () => {
    // Flujo unificado: nativo o abre el modal de instrucciones. En ambos casos
    // ocultamos el banner (si se abrió el modal, este queda visible aparte).
    setShow(false);
    await install.trigger();
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now() + SNOOZE_MS));
    setShow(false);
    // Reaparecerá automáticamente al expirar el snooze, sin esperar recarga.
    scheduleShow(SNOOZE_MS);
  };

  return (
    <>
      {show && !install.hidden && (
        <div className="install-backdrop" onClick={handleDismiss}>
          <div className="install-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="install-handle" />

            <div className="install-header">
              <div className="install-header__glow" />
              <img src="/logo_white.png" alt="Aldahra" className="install-logo" />
              <div className="install-header__text">
                <span className="install-appname">App Controllers</span>
                <span className="install-tagline">Aldahra Farming Spain</span>
              </div>
            </div>

            {iosBlocked ? (
              <div className="install-body">
                <p className="install-title">{t('components.install.syncFirstTitle')}</p>
                <p className="install-desc">
                  {t('components.install.syncFirstDesc', { loads: t('components.install.loadPending', { count: pendingCount }) })}
                </p>
                <button className="install-btn install-btn--secondary" onClick={handleDismiss}>
                  {t('components.install.understood')}
                </button>
              </div>
            ) : (
              <div className="install-body">
                <p className="install-title">
                  {pendingCount > 0
                    ? t('components.install.pendingTitle', { loads: t('components.install.loadPending', { count: pendingCount }) })
                    : t('components.install.defaultTitle')}
                </p>
                <p className="install-desc">
                  {pendingCount > 0
                    ? t('components.install.pendingDesc')
                    : t('components.install.defaultDesc')}
                </p>

                <div className="install-features">
                  <div className="install-feature">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
                    <span>{t('components.install.featureOffline')}</span>
                  </div>
                  <div className="install-feature">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/></svg>
                    <span>{t('components.install.featureFullscreen')}</span>
                  </div>
                  <div className="install-feature">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M13 2.05V4.05C17.39 4.59 20.5 8.58 19.96 12.97C19.5 16.61 16.64 19.5 13 19.93V21.93C18.5 21.38 22.5 16.5 21.95 11C21.5 6.25 17.73 2.5 13 2.05M11 2.06C9.05 2.25 7.19 3 5.67 4.26L7.1 5.74C8.22 4.84 9.57 4.26 11 4.06V2.06M4.26 5.67C3 7.19 2.25 9.04 2.05 11H4.05C4.24 9.58 4.8 8.23 5.69 7.1L4.26 5.67M2.06 13C2.26 14.96 3.03 16.81 4.27 18.33L5.69 16.9C4.81 15.77 4.24 14.42 4.06 13H2.06M7.1 18.37L5.67 19.74C7.18 21 9.04 21.79 11 22V20C9.58 19.82 8.23 19.25 7.1 18.37Z"/></svg>
                    <span>{t('components.install.featureUpdated')}</span>
                  </div>
                </div>

                <div className="install-actions">
                  <button className="install-btn install-btn--secondary" onClick={handleDismiss}>
                    {t('components.install.notNow')}
                  </button>
                  <button className="install-btn install-btn--primary" onClick={handleInstall}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
                    {t('components.install.installApp')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <InstallInstructions open={install.modalOpen} onClose={install.closeModal} />
    </>
  );
}
